import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import { ConsentStatus, TenantDetail } from '../../api/tenantRegistry';
import ConsentStatusChip from '../../components/ConsentStatusChip';
import RelativeTime from '../../components/RelativeTime';
import SectionCard from '../../components/SectionCard';
import { Fact, Mono, SectionLabel } from '../../components/detailPrimitives';
import { useTenantDetailQuery } from '../../hooks/useTenantDetail';
import { ACCENT, TEXT } from '../../theme/tokens';
import GrantPermissionsButton from '../../components/tenant/GrantPermissionsButton';

// Overview & consent tab (FD-158, ADR-023). Consent verdict, missing roles, the
// Trigger-consent action, and the freshness stamps. Re-check lives in the shared
// TenantLayout header (present on every tab). Full identifiers surfaced (§5);
// secrets never (§2 — this tab shows none).

const CONSENT_COPY: Record<ConsentStatus, string> = {
  consented:
    'This tenant has consented all required Microsoft Graph permissions. No action is needed.',
  insufficient:
    'This tenant is missing one or more required Graph permissions. An admin of the tenant must grant consent before migration operations against it can run.',
  unknown:
    'The consent state could not be verified on the last check — a Graph read failed. This is not the same as missing consent. Re-check to try again.',
};

/**
 * One line per prerequisite, each with the tab that can act on it.
 *
 * Three tones, never two. `warn` is "look at this" and `unknown` is "we could not
 * tell" — collapsing the second into either of the others is the same mistake the
 * certificate state machine exists to prevent, and a cache that hides its age gets
 * believed exactly like a credential does.
 */
function ReadinessSummary({ tenant }: { tenant: TenantDetail }) {
  const isDestination = tenant.roles.some((r) => r.toLowerCase() === 'destination');
  const cert = tenant.tenantAppCertificate;
  const hasApp = Boolean(tenant.tenantAppClientId?.trim()) || tenant.servicePrincipals.length > 0;

  const lines: { tone: 'ok' | 'warn' | 'unknown'; text: string; to: string }[] = [];

  lines.push(
    tenant.consentStatus === 'consented'
      ? { tone: 'ok', text: 'Graph permissions consented', to: '' }
      : tenant.consentStatus === 'insufficient'
        ? { tone: 'warn', text: 'Graph consent incomplete — an admin of this tenant must grant it', to: '' }
        : { tone: 'unknown', text: 'Consent could not be verified on the last check', to: '' },
  );

  lines.push(
    hasApp
      ? { tone: 'ok', text: 'Tenant application resolves', to: 'application' }
      : {
          tone: 'warn',
          text: 'No tenant application — every Graph operation against this tenant fails',
          to: 'application',
        },
  );

  if (cert.state !== 'not-configured') {
    lines.push(
      cert.state === 'healthy'
        ? { tone: 'ok', text: 'Certificate current', to: 'application' }
        : cert.state === 'due'
          ? {
              tone: 'warn',
              text:
                cert.daysRemaining !== null && cert.daysRemaining < 0
                  ? 'Certificate has EXPIRED — rotation needs a customer admin sign-in'
                  : 'Certificate rotation due — it needs a customer admin sign-in',
              to: 'application',
            }
          : { tone: 'unknown', text: 'Certificate expiry not tracked for this application', to: 'application' },
    );
    if (cert.rotationStatus === 'failed') {
      lines.push({ tone: 'warn', text: 'The last certificate rotation did not complete', to: 'application' });
    }
  }

  if (isDestination) {
    const sa = tenant.bprtServiceAccount;
    lines.push(
      sa?.present
        ? { tone: 'ok', text: 'Minting service account present', to: 'service-accounts' }
        : sa === null
          ? { tone: 'unknown', text: 'No minting-account snapshot yet', to: 'service-accounts' }
          : {
              tone: 'warn',
              text: 'Minting service account not found — wave activation would have nobody to sign in as',
              to: 'service-accounts',
            },
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {lines.map((line) => (
        <span key={line.text} className="inline-flex items-start gap-2 font-display text-sm">
          <span
            aria-hidden="true"
            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
              line.tone === 'ok' ? 'bg-green-600' : line.tone === 'warn' ? 'bg-amber-500' : 'bg-zinc-400'
            }`}
          />
          <span style={{ color: line.tone === 'ok' ? TEXT.secondary : TEXT.primary }}>
            {line.text}
            {line.to && (
              <>
                {' '}
                <Link
                  to={`../${line.to}`}
                  relative="path"
                  style={{ color: ACCENT.pressed, textDecorationLine: 'none' }}
                >
                  Open
                </Link>
              </>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}

function titleCaseRole(role: string): string {
  if (!role) return role;
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

export default function TenantOverviewPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const query = useTenantDetailQuery(tenantId);
  const tenant = query.data;
  if (!tenant) return null;

  const canTriggerConsent =
    tenant.consentStatus !== 'consented' && Boolean(tenant.grantUrl || tenant.consentUrl);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="flex flex-col gap-10"
    >
      {/* Consent */}
      <section>
        <SectionLabel>Consent</SectionLabel>

        <SectionCard>
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <ConsentStatusChip status={tenant.consentStatus} />
          </div>

          <p className="font-display text-sm text-zinc-600 leading-relaxed max-w-[65ch]">
            {CONSENT_COPY[tenant.consentStatus]}
          </p>

          {tenant.consentStatus === 'insufficient' && tenant.missingRoles.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="font-display text-[11px] font-medium uppercase tracking-[0.06em] text-zinc-400">
                Missing roles
              </span>
              <div className="flex flex-wrap gap-2">
                {tenant.missingRoles.map((role) => (
                  <span
                    key={role}
                    className="font-mono text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          {canTriggerConsent && (
            <div>
              <GrantPermissionsButton tenant={tenant} />
              <p className="font-display text-xs text-zinc-500 mt-2 max-w-[60ch]">
                {tenant.grantUrl
                  ? 'Opens Microsoft’s consent page in this tab. A Global or Privileged Role Administrator approves, and you come straight back here to a fresh verdict.'
                  : 'Opens the Microsoft admin-consent flow in a new tab. After granting, use Re-check to refresh this verdict.'}
              </p>
            </div>
          )}
        </div>
        </SectionCard>
      </section>

      {/* FD-531 — readiness, in one place, from facts already on screen.
          ───────────────────────────────────────────────────────────────────────
          THE GAP THIS CLOSES, and why it is not `TenantReadinessPanel`.

          That component exists for the create-migration wizard, where there is no
          registry context: it runs a LIVE consent probe and a registry lookup for a
          tenant id somebody has just typed. Mounting it here — the literal reading
          of FD-531's brief — would put a SECOND, differently-sourced consent verdict
          directly beside the snapshot one this page already renders, and the two can
          disagree. Two renderings of one verdict is how they come to disagree
          (FD-ADR-043's own reasoning for refusing a second preflight card), and it
          would add a Graph call to a detail-page load for facts that are already in
          the response.

          The REAL gap it names is true and is what this closes: a tenant's readiness
          is spread across four tabs, so the page whose subject is this tenant cannot
          answer "is it ready" at a glance. This derives that from the loaded view —
          zero extra calls, one source, each line linking to the tab that can act. */}
      <section>
        <SectionLabel>Readiness</SectionLabel>
        <SectionCard>
          <ReadinessSummary tenant={tenant} />
        </SectionCard>
      </section>

      {/* Freshness + identity */}
      <section>
        <SectionLabel>Registry snapshot</SectionLabel>
        <SectionCard>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-5">
          <Fact label="Tenant ID">
            <Mono>{tenant.tenantId}</Mono>
          </Fact>
          <Fact label="Domain">
            {tenant.tenantDomain?.trim() ? (
              <span className="text-zinc-800">{tenant.tenantDomain}</span>
            ) : (
              <span className="text-zinc-400">—</span>
            )}
          </Fact>
          <Fact label="Display name">
            {tenant.displayName?.trim() ? (
              <span className="text-zinc-800">{tenant.displayName}</span>
            ) : (
              <span className="text-zinc-400">—</span>
            )}
          </Fact>
          <Fact label="Roles">
            {tenant.roles.length > 0 ? (
              <span className="text-zinc-800">{tenant.roles.map(titleCaseRole).join(' · ')}</span>
            ) : (
              <span className="text-zinc-400">—</span>
            )}
          </Fact>
          <Fact label="Consent checked">
            <span className="text-zinc-800">
              <RelativeTime iso={tenant.consentCheckedUtc} emptyPlaceholder="never" />
            </span>
          </Fact>
          <Fact label="Snapshot refreshed">
            <span className="text-zinc-800">
              <RelativeTime iso={tenant.refreshedUtc} emptyPlaceholder="never" />
            </span>
          </Fact>
          <Fact label="First seen">
            <span className="text-zinc-800">
              <RelativeTime iso={tenant.firstSeenUtc} emptyPlaceholder="—" />
            </span>
          </Fact>
          <Fact label="Last seen">
            <span className="text-zinc-800">
              <RelativeTime iso={tenant.lastSeenUtc} emptyPlaceholder="—" />
            </span>
          </Fact>
        </dl>
        </SectionCard>
      </section>
    </motion.div>
  );
}
